import { getLineItemName } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatUsd } from '@/Client/Presenters';
import {
  getEffectiveScanValueAsNumber,
  getLineItemsInStandardSortOrder,
  getOptionsInStandardSortOrder,
  getTotalCostIfDefined,
  isLineItemScoped,
  isOptionScoped,
  type ScanLineItemOptimized,
  type ScanOptionOptimized,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

/**
 * What a task is actually made of, put into words: its scoped options and the scoped line items under
 * each.
 *
 * Grouped rather than flattened. `getTaskLineItemRows` in the closeout builders walks the same
 * `options[] -> lineItems[]` structure but returns one flat list carrying an `optionId`, which suits a
 * table; the photo view treats options as sub-headings, so it needs the nesting kept.
 *
 * Scoped means contributing cost, by `isOptionScoped` / `isLineItemScoped` — the same test the scope
 * report uses, so the view can never disagree with the report about what is in the scope.
 */

/** One product on the scope: enough to recognise it and check the quantity and the money. */
export type ProgressAltLineItem = {
  id: string;
  title: string;
  /** The product's own catalogue photo, when it carries one. */
  imageUrl?: string;
  quantityLabel: string;
  subtotalLabel?: string;
  /**
   * The catalogue product behind this line item, if there is one. Carried as an id rather than resolved
   * here: the lookup is a hook, so it belongs to the row that renders it, not to this transform.
   */
  productSkuId?: string;
};

/** One option of a task, standing as a sub-heading over the products it calls for. */
export type ProgressAltScopeOption = {
  id: string;
  title: string;
  /** The option template's own prose. Long enough to need collapsing, often enough to be worth showing. */
  description?: string;
  lineItems: readonly ProgressAltLineItem[];
};

/** Mirrors the group and task presenters: the template's name, with any custom label in parentheses. */
const getOptionTitle = (option: ScanOptionOptimized): string => {
  const name = option.template?.name?.trim() || 'Option';
  return option.customLabel ? `${name} (${option.customLabel})` : name;
};

/**
 * Quantity with its unit. The template's value type is the unit — "sqft", "ft" — and a line item with
 * no stated type is counted in pieces.
 */
const getQuantityLabel = (lineItem: ScanLineItemOptimized): string => {
  const quantity = getEffectiveScanValueAsNumber(lineItem.values?.quantity) ?? 1;
  return `${quantity} ${lineItem.template?.values?.quantity?.type || 'each'}`;
};

/** Mirrors `getPartCostValue`'s guard: fixtures and partial inputs may carry no `reducedValues`. */
const getLineItemSubtotal = (lineItem: ScanLineItemOptimized): number | undefined =>
  lineItem.optimized && !lineItem.optimized.reducedValues ? undefined : getTotalCostIfDefined(lineItem);

/**
 * Which catalogue product this line item stands for: the one chosen on the scan, or failing that the
 * template's first offered option. The same fallback the inline line-item editor uses, so both surfaces
 * open the same product for the same row.
 */
const getProductSkuId = (lineItem: ScanLineItemOptimized): string | undefined => {
  if (lineItem.skuId) return lineItem.skuId;
  const [firstOption] = lineItem.template?.skuIdOptions ?? [];
  if (!firstOption) return undefined;
  return typeof firstOption === 'string' ? firstOption : firstOption.id;
};

const getLineItem = (lineItem: ScanLineItemOptimized): ProgressAltLineItem => {
  const subtotal = getLineItemSubtotal(lineItem);
  return {
    id: lineItem.id,
    title: getLineItemName(lineItem),
    imageUrl: lineItem.skuDetails?.imageUrl,
    quantityLabel: getQuantityLabel(lineItem),
    subtotalLabel: subtotal != null ? formatUsd(subtotal) : undefined,
    productSkuId: getProductSkuId(lineItem),
  };
};

/**
 * The task's scoped options, each with its scoped line items, in the order the scope report puts them.
 *
 * An option that ends up with no line items still comes through: it is scoped, so it is costing the
 * project something, and a sub-heading with nothing under it says that more usefully than silence.
 */
export const getProgressAltScopeOptions = (
  task: ScanTaskOptimized | undefined,
): readonly ProgressAltScopeOption[] => {
  if (!task?.template) return [];
  return getOptionsInStandardSortOrder(task.template, task.options ?? [], isOptionScoped).map(option => ({
    id: option.id,
    title: getOptionTitle(option),
    description: option.template?.description?.trim() || undefined,
    lineItems: getLineItemsInStandardSortOrder(option.template, option.lineItems ?? [], isLineItemScoped)
      .map(getLineItem),
  }));
};
